import { useEffect, useState, useCallback, useMemo, Component } from "react";
import { Spinner, Container, Row, Col, Form, Button, Alert, Toast, ToastContainer, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaWallet, FaSort, FaPercentage, FaTag, FaListOl, FaSync, FaSearch, FaTimes, FaFileCsv, FaLink, FaCopy } from "react-icons/fa";
import { Sparklines, SparklinesLine } from "react-sparklines";
import DOMPurify from "dompurify";
import debounce from "lodash.debounce";
import { API_SERVER } from "../explorer_constants";
import { getCoinSupply } from "../vecno-api-client";
import UtxoPagination from "./UtxoPagination";
import addressTags from "./addressTags";

// Configuration constants
const CONFIG = {
  API_BASE: `${API_SERVER}/`,
  WALLETS_PER_PAGE: 10,
  ITEMS_PER_PAGE: 1000,
  CACHE_EXPIRY_MS: 24 * 60 * 60 * 1000, // 24 hours
  DEBOUNCE_MS: 300,
  MAX_HISTORY_DAYS: 7,
  REFRESH_COOLDOWN_MS: 30000, // 30 seconds cooldown for refresh
};

// Animal symbols for distribution ranges
const animalSymbols = {
  "5M+ VE": { name: "Blue Whale", icon: "🐳", className: "range-5m" },
  "1M-5M VE": { name: "Orca", icon: "🐬", className: "range-1m-5m" },
  "500K-1M VE": { name: "Great White Shark", icon: "🦈", className: "range-500k-1m" },
  "100K-500K VE": { name: "Manta Ray", icon: "🐠", className: "range-100k-500k" },
  "10K-100K VE": { name: "Sea Turtle", icon: "🐢", className: "range-10k-100k" },
  "1000-10K VE": { name: "Clownfish", icon: "🐟", className: "range-1k-10k" },
  "100-1000 VE": { name: "Seashell", icon: "🐚", className: "range-100-1k" },
  "10-100 VE": { name: "Coral", icon: "🪸", className: "range-10-100" },
  "1-10 VE": { name: "Plankton", icon: "🦠", className: "range-1-10" },
};

// Utility functions
const truncateAddress = (address) => {
  if (!address || address.length < 14) return address;
  const prefix = "vecno:";
  const body = address.slice(prefix.length);
  return `${prefix}${body.slice(0, 8)}...${body.slice(-4)}`;
};

const copyToClipboard = async (text, setToastMessage, setToastVariant, setShowToast) => {
  if (!window.isSecureContext) {
    setToastMessage("Clipboard access requires a secure context (https or localhost).");
    setToastVariant("danger");
    setShowToast(true);
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
    setToastMessage("Copied to clipboard!");
    setToastVariant("success");
    setShowToast(true);
  } catch (err) {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setToastMessage("Copied to clipboard!");
      setToastVariant("success");
      setShowToast(true);
    } catch (fallbackErr) {
      console.error("Clipboard copy failed:", fallbackErr);
      setToastMessage("Failed to copy. Please copy manually.");
      setToastVariant("danger");
      setShowToast(true);
    }
  }
};

const fetchWithRetry = async (url, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          throw new Error(`Client error: ${response.statusText}`);
        }
        throw new Error(`API error: ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
  throw new Error("Max retries reached");
};

// Number formatting utility
const formatAmount = (amount) => {
  return Math.round(Number(amount)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Custom throttle function
const throttle = (func, wait) => {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= wait) {
      lastCall = now;
      return func(...args);
    }
  };
};

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    console.error("ErrorBoundary caught:", error);
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container className="fourthRow webpage" fluid>
          <Row>
            <Col xs={12}>
              <div className="table-card">
                <h4>Something went wrong.</h4>
                <p>{this.state.error?.message || "An unexpected error occurred."}</p>
                <Button variant="primary" onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      );
    }
    return this.props.children;
  }
}

// Skeleton Row for loading state
const SkeletonRow = ({ columns }) => (
  <tr>
    {Array.from({ length: columns }).map((_, index) => (
      <td key={index} data-label="">
        <div className="skeleton" />
      </td>
    ))}
  </tr>
);

const TopHolders = () => {
  // State declarations
  const [wallets, setWallets] = useState([]);
  const [totalAddresses, setTotalAddresses] = useState(0);
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageError, setPageError] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [totalSupply, setTotalSupply] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [sortField, setSortField] = useState("amount");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTag, setSelectedTag] = useState("All");
  const [selectedRange, setSelectedRange] = useState("All");
  const [isExporting, setIsExporting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [isSearchValid, setIsSearchValid] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(CONFIG.WALLETS_PER_PAGE);
  const [lastRefreshTime, setLastRefreshTime] = useState(0); // Track last refresh time
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Distribution ranges
  const distributionRanges = useMemo(() => [
    { min: 5000000, max: 0, label: "5M+ VE" },
    { min: 1000000, max: 5000000, label: "1M-5M VE" },
    { min: 500000, max: 1000000, label: "500K-1M VE" },
    { min: 100000, max: 500000, label: "100K-500K VE" },
    { min: 10000, max: 100000, label: "10K-100K VE" },
    { min: 1000, max: 10000, label: "1000-10K VE" },
    { min: 100, max: 1000, label: "100-1000 VE" },
    { min: 10, max: 100, label: "10-100 VE" },
    { min: 1, max: 10, label: "1-10 VE" },
  ], []);

  // Unique tags for filtering
  const uniqueTags = useMemo(() => {
    const tags = new Set(Object.values(addressTags).filter((tag) => !!tag));
    return ["All", ...tags];
  }, []);

  // Sorted wallets with ranks
  const sortedWalletsWithRanks = useMemo(() => {
    const sorted = [...wallets].sort((a, b) => {
      const multiplier = sortOrder === "asc" ? 1 : -1;
      return multiplier * (a[sortField] - b[sortField]);
    });
    return sorted.map((wallet, index) => ({
      ...wallet,
      rank: index + 1,
    }));
  }, [wallets, sortField, sortOrder]);

  // Filtered wallets based on tag and range
  const filteredWallets = useMemo(() => {
    let filtered = sortedWalletsWithRanks;
    if (selectedTag !== "All") {
      filtered = filtered.filter((wallet) => addressTags[wallet.address] === selectedTag);
    }
    if (selectedRange !== "All") {
      const range = distributionRanges.find((r) => r.label === selectedRange);
      if (range) {
        filtered = filtered.filter((wallet) => {
          const balance = wallet.amount;
          return range.max === 0 ? balance >= range.min : balance >= range.min && balance < range.max;
        });
      }
    }
    return filtered;
  }, [sortedWalletsWithRanks, selectedTag, selectedRange, distributionRanges]);

  // Paginated wallets
  const filteredPaginatedWallets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredWallets.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredWallets, currentPage, itemsPerPage]);

  // Total pages for pagination
  const totalFilteredPages = useMemo(() =>
    Math.ceil(filteredWallets.length / itemsPerPage),
    [filteredWallets, itemsPerPage]
  );

  // Fetch wallet data and supply
  const fetchData = useCallback(async () => {
    const start = performance.now();
    try {
      setLoading(true);
      setApiError(null);
      setCurrentPage(1);

      const previousTimestamp = localStorage.getItem("vecnoTopHoldersTimestamp");
      const now = Date.now();
      if (previousTimestamp && now - parseInt(previousTimestamp) > CONFIG.CACHE_EXPIRY_MS) {
        localStorage.removeItem("vecnoTopHoldersBalances");
        localStorage.removeItem("vecnoTopHoldersTimestamp");
        localStorage.removeItem("vecnoTopHoldersHistory");
      }

      const previousData = localStorage.getItem("vecnoTopHoldersBalances");
      const previousBalances = previousData ? JSON.parse(previousData) : null;
      const historyData = localStorage.getItem("vecnoTopHoldersHistory");
      let balanceHistory = historyData ? JSON.parse(historyData) : {};

      const coinSupplyResp = await getCoinSupply();
      if (!coinSupplyResp?.circulatingSupply) {
        throw new Error("Invalid circulating supply response.");
      }
      const supply = Math.round(coinSupplyResp.circulatingSupply / 100000000);
      setTotalSupply(supply);

      const data = await fetchWithRetry(
        `${CONFIG.API_BASE}addresses/balances/csv/paged?page=1&items_per_page=${CONFIG.ITEMS_PER_PAGE}`
      );
      const rows = data.trim().split("\n").slice(1);
      if (rows.length === 0) {
        throw new Error("No address data returned");
      }

      const parsedWallets = rows
        .map((row, index) => {
          const [address, balance] = row.split(",");
          if (!address || isNaN(parseFloat(balance))) {
            console.warn(`Invalid data at row ${index + 2}: ${row}`);
            return null;
          }
          const amount = parseFloat(balance) / 100000000;
          const percent = (amount / supply) * 100;
          return { address, amount, percent, history: balanceHistory[address] || [] };
        })
        .filter((wallet) => !!wallet);

      // Update balance history
      const today = new Date().toISOString().split("T")[0];
      parsedWallets.forEach((wallet) => {
        if (!balanceHistory[wallet.address]) {
          balanceHistory[wallet.address] = [];
        }
        balanceHistory[wallet.address].push({ date: today, amount: wallet.amount });
        balanceHistory[wallet.address] = balanceHistory[wallet.address].slice(-CONFIG.MAX_HISTORY_DAYS);
      });

      const walletsWithTrends = parsedWallets.map((wallet) => {
        if (!previousBalances) {
          return { ...wallet, trend: "no-change", changePercent: 0 };
        }
        const previousWallet = previousBalances.find((w) => w.address === wallet.address);
        if (!previousWallet) {
          return { ...wallet, trend: "no-change", changePercent: 0 };
        }
        const previousAmount = previousWallet.amount;
        const currentAmount = wallet.amount;
        let trend = "no-change";
        let changePercent = 0;
        if (previousAmount !== 0) {
          changePercent = ((currentAmount - previousAmount) / previousAmount) * 100;
        }
        if (currentAmount > previousAmount) {
          trend = "up";
        } else if (currentAmount < previousAmount) {
          trend = "down";
        }
        return { ...wallet, trend, changePercent };
      });

      // Check localStorage size and clean up if necessary
      const storageData = JSON.stringify({ wallets: walletsWithTrends, history: balanceHistory });
      if (storageData.length > 4 * 1024 * 1024) {
        console.warn("LocalStorage nearing limit, cleaning up old data");
        localStorage.removeItem("vecnoTopHoldersHistory");
        balanceHistory = {};
      }

      localStorage.setItem("vecnoTopHoldersBalances", JSON.stringify(walletsWithTrends));
      localStorage.setItem("vecnoTopHoldersTimestamp", now.toString());
      localStorage.setItem("vecnoTopHoldersHistory", JSON.stringify(balanceHistory));

      const sortedWallets = walletsWithTrends.sort((a, b) => b.amount - a.amount);
      setWallets(sortedWallets);
      setTotalAddresses(sortedWallets.length);

      // Updated distribution calculation
      const distributionData = distributionRanges.map((range) => {
        const addressesInRange = sortedWallets.filter((addr) => {
          const balance = addr.amount;
          return range.max === 0 ? balance >= range.min : balance >= range.min && balance < range.max;
        });
        const totalBalanceInRange = addressesInRange.reduce((sum, addr) => sum + addr.amount, 0);
        return {
          count: addressesInRange.length,
          totalBalance: totalBalanceInRange,
        };
      });

      const distributions = distributionRanges.map((item, index) => ({
        ...item,
        value: distributionData[index].count,
        percentage: supply ? (distributionData[index].totalBalance / supply) * 100 : 0,
      }));

      setDistributions(distributions);
      setLastUpdated(new Date());
      setLoading(false);
      console.log(`Data fetch took ${performance.now() - start}ms`);
    } catch (error) {
      console.error("Fetch error:", error);
      setApiError(error instanceof Error ? error.message : "An error occurred while fetching data.");
      setLoading(false);
      setPageError(true);
    }
  }, [distributionRanges]);

  // Throttled fetchData with 30-second cooldown
  const throttledFetchData = useMemo(() => throttle(fetchData, CONFIG.REFRESH_COOLDOWN_MS), [fetchData]);

  // Search function
  const performSearch = useCallback((query) => {
    const trimmedQuery = query.trim().toLowerCase();
    if (!trimmedQuery) {
      setSearchResults([]);
      setIsSearchValid(true);
      return;
    }
    const addressRegex = /^vecno:[a-zA-Z0-9]{61,63}$/;
    setIsSearchValid(addressRegex.test(trimmedQuery) || trimmedQuery.length < 10);
    const results = sortedWalletsWithRanks.filter((wallet) =>
      wallet.address.toLowerCase().includes(trimmedQuery)
    );
    setSearchResults(results);
  }, [sortedWalletsWithRanks]);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => performSearch(query), CONFIG.DEBOUNCE_MS),
    [performSearch]
  );

  // Cleanup debouncedSearch on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Initial data fetch and search param handling
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const address = searchParams.get("address");
    if (address) {
      const sanitizedAddress = DOMPurify.sanitize(address);
      setSearchQuery(sanitizedAddress);
      performSearch(sanitizedAddress);
    }
  }, [searchParams, performSearch]);

  // Handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalFilteredPages) {
      setCurrentPage(page);
      setPageError(false);
    } else {
      setPageError(true);
    }
  }, [totalFilteredPages]);

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNo = parseInt(DOMPurify.sanitize(e.currentTarget.elements.namedItem("pageNo").value), 10);
    if (!isNaN(pageNo) && pageNo > 0) {
      handlePageChange(pageNo);
    } else {
      setPageError(true);
    }
  };

  const handleSort = useMemo(
    () => debounce((field) => {
      setSortField(field);
      setSortOrder((prev) => (prev === "asc" && sortField === field ? "desc" : "asc"));
    }, CONFIG.DEBOUNCE_MS),
    [sortField]
  );

  // Cleanup handleSort on unmount
  useEffect(() => {
    return () => {
      handleSort.cancel();
    };
  }, [handleSort]);

  const handleRefresh = () => {
    const now = Date.now();
    if (now - lastRefreshTime < CONFIG.REFRESH_COOLDOWN_MS) {
      const remainingSeconds = Math.ceil((CONFIG.REFRESH_COOLDOWN_MS - (now - lastRefreshTime)) / 1000);
      setToastMessage(`Please wait ${remainingSeconds} seconds before refreshing again.`);
      setToastVariant("warning");
      setShowToast(true);
      return;
    }
    setLastRefreshTime(now);
    throttledFetchData();
  };

  const handleExportToCSV = async () => {
    try {
      setIsExporting(true);
      const csvData = [
        "rank,address,balance,tag",
        ...filteredWallets.map((w) => `${w.rank},${w.address},${w.amount * 100000000},${addressTags[w.address] || ""}`),
      ].join("\n");
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "top-holders.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToastMessage("Exported to CSV successfully!");
      setToastVariant("success");
      setShowToast(true);
    } catch (error) {
      console.error("Export error:", error);
      setApiError("Failed to export data to CSV. Please try again later.");
      setToastMessage("Failed to export CSV.");
      setToastVariant("danger");
      setShowToast(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(searchQuery);
  };

  const handleSearchInputChange = (e) => {
    const query = DOMPurify.sanitize(e.target.value);
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearchValid(true);
    } else {
      debouncedSearch(query);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearchValid(true);
  };

  // Check if refresh is disabled
  const isRefreshDisabled = loading || Date.now() - lastRefreshTime < CONFIG.REFRESH_COOLDOWN_MS;

  // Calculate remaining cooldown time for button text
  const remainingCooldownSeconds = isRefreshDisabled && !loading
    ? Math.ceil((CONFIG.REFRESH_COOLDOWN_MS - (Date.now() - lastRefreshTime)) / 1000)
    : 0;

  // Empty state
  if (!loading && wallets.length === 0 && !apiError) {
    return (
      <Container className="fourthRow webpage" fluid>
        <Row>
          <Col xs={12}>
            <div className="table-card">
              <Alert variant="info">
                No wallet data available. Please try refreshing or contact support.
              </Alert>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <div className="top-holders-page">
        <Container className="fourthRow webpage" fluid>
          <Row>
            <Col xs={12}>
              <div className="table-card">
                <h3 className="section-header">Top Holders</h3>
                <div className="block-overview-content">
                  {loading ? (
                    <div className="text-center">
                      <Spinner animation="border" role="status" className="mb-3">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <div className="table-responsive">
                        <table className="table-common top-table w-100 mb-4 table-sticky">
                          <thead>
                            <tr>
                              <th scope="col"><FaSort className="me-1" /> Range</th>
                              <th scope="col"><FaWallet className="me-1" /> Addresses</th>
                              <th scope="col"><FaPercentage className="me-1" /> % of Supply</th>
                              <th scope="col">Symbol</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: 5 }).map((_, index) => (
                              <SkeletonRow key={index} columns={4} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="table-responsive">
                        <table className="table-common styled-table w-100 mt-2 table-sticky">
                          <thead>
                            <tr>
                              <th scope="col"><FaListOl className="me-1" /> Rank</th>
                              <th scope="col"><FaWallet className="me-1" /> Amount</th>
                              <th scope="col"><FaPercentage className="me-1" /> Supply</th>
                              <th scope="col">Address</th>
                              <th scope="col"><FaTag className="me-1" /> Tags</th>
                              <th scope="col" className="hide-on-mobile">Trend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: 5 }).map((_, index) => (
                              <SkeletonRow key={index} columns={6} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <>
                      {apiError && (
                        <Alert variant="danger" className="mb-4">
                          Error: {apiError}.{" "}
                          <Button variant="link" onClick={handleRefresh}>
                            Try again
                          </Button>
                        </Alert>
                      )}
                      <div className="blockinfo-summary">
                        <p>Total Addresses: {totalAddresses}</p>
                        <p>Circulating Supply: {formatAmount(totalSupply)}</p>
                        <p className="last-updated">Last Updated: {lastUpdated.toLocaleString()}</p>
                        <Button
                          variant="primary"
                          onClick={handleRefresh}
                          className="mt-2"
                          disabled={isRefreshDisabled}
                          aria-label="Refresh data"
                        >
                          <FaSync className="me-1" />{" "}
                          {loading
                            ? "Refreshing..."
                            : isRefreshDisabled
                            ? `Refresh (${remainingCooldownSeconds}s)`
                            : "Refresh"}
                        </Button>
                      </div>
                      <h5 className="section-header mt-4">Distribution of Holders (Top {CONFIG.ITEMS_PER_PAGE} Addresses)</h5>
                      <div className="table-responsive">
                        <table className="table-common top-table w-100 mb-4 table-sticky">
                          <thead>
                            <tr>
                              <th scope="col"><FaSort className="me-1" /> Range</th>
                              <th scope="col"><FaWallet className="me-1" /> Addresses</th>
                              <th scope="col"><FaPercentage className="me-1" /> % of Supply</th>
                              <th scope="col">Symbol</th>
                            </tr>
                          </thead>
                          <tbody>
                            {distributions.map(({ label, value, percentage }) => (
                              <tr key={label} className="fade-in-row">
                                <td data-label="Range">{label}</td>
                                <td data-label="Addresses">{value}</td>
                                <td data-label="% of Supply">{percentage ? percentage.toFixed(2) : 0}%</td>
                                <td data-label="Symbol">
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>{animalSymbols[label]?.name || "Unknown"}</Tooltip>}
                                  >
                                    <Badge bg="none" className={`symbol badge-tag ${animalSymbols[label]?.className || ""}`}>
                                      {animalSymbols[label]?.icon || "❓"}
                                    </Badge>
                                  </OverlayTrigger>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <h5 className="section-header mt-4">Top Wallets</h5>
                      <Row className="mt-4 mb-2">
                        <Col xs={12} sm={4}>
                          <Form.Group controlId="tagFilter">
                            <Form.Label>Filter by Tag:</Form.Label>
                            <Form.Select
                              value={selectedTag}
                              onChange={(e) => {
                                setSelectedTag(e.target.value);
                                setCurrentPage(1);
                              }}
                              aria-label="Filter by address tag"
                            >
                              {uniqueTags.map((tag) => (
                                <option key={tag} value={tag}>
                                  {tag}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12} sm={4}>
                          <Form.Group controlId="rangeFilter">
                            <Form.Label>Filter by Balance Range:</Form.Label>
                            <Form.Select
                              value={selectedRange}
                              onChange={(e) => {
                                setSelectedRange(e.target.value);
                                setCurrentPage(1);
                              }}
                              aria-label="Filter by balance range"
                            >
                              <option value="All">All</option>
                              {distributionRanges.map((range) => (
                                <option key={range.label} value={range.label}>
                                  {range.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12} sm={4}>
                          <Form.Group controlId="itemsPerPage">
                            <Form.Label>Items per Page:</Form.Label>
                            <Form.Select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              aria-label="Select items per page"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12} className="mt-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedTag("All");
                              setSelectedRange("All");
                              setItemsPerPage(CONFIG.WALLETS_PER_PAGE);
                              setCurrentPage(1);
                            }}
                            aria-label="Reset filters"
                            disabled={selectedTag === "All" && selectedRange === "All" && itemsPerPage === CONFIG.WALLETS_PER_PAGE}
                          >
                            Reset Filters
                          </Button>
                        </Col>
                      </Row>
                      <div className="table-responsive">
                        <table className="table-common styled-table w-100 mt-2 table-sticky">
                          <thead>
                            <tr>
                              <th scope="col"><FaListOl className="me-1" /> Rank</th>
                              <th
                                scope="col"
                                onClick={() => handleSort("amount")}
                                onKeyDown={(e) => e.key === "Enter" && handleSort("amount")}
                                style={{ cursor: "pointer" }}
                                role="button"
                                tabIndex={0}
                                aria-label={`Sort by amount, currently sorted ${sortField === "amount" ? (sortOrder === "asc" ? "ascending" : "descending") : "unsorted"}`}
                              >
                                <FaWallet className="me-1" /> Amount {sortField === "amount" && (sortOrder === "asc" ? "↑" : "↓")}
                              </th>
                              <th
                                scope="col"
                                onClick={() => handleSort("percent")}
                                onKeyDown={(e) => e.key === "Enter" && handleSort("percent")}
                                style={{ cursor: "pointer" }}
                                role="button"
                                tabIndex={0}
                                aria-label={`Sort by supply percentage, currently sorted ${sortField === "percent" ? (sortOrder === "asc" ? "ascending" : "descending") : "unsorted"}`}
                              >
                                <FaPercentage className="me-1" /> Supply {sortField === "percent" && (sortOrder === "asc" ? "↑" : "↓")}
                              </th>
                              <th scope="col">Address</th>
                              <th scope="col"><FaTag className="me-1" /> Tags</th>
                              <th scope="col" className="hide-on-mobile">Trend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredPaginatedWallets.length > 0 ? (
                              filteredPaginatedWallets.map((wallet) => (
                                <tr
                                  key={wallet.address}
                                  onClick={() => navigate(`/addresses/${wallet.address}`)}
                                  onKeyDown={(e) => e.key === "Enter" && navigate(`/addresses/${wallet.address}`)}
                                  style={{ cursor: "pointer" }}
                                  className={`fade-in-row ${
                                    searchResults.length > 0 && wallet.address === searchResults[0]?.address
                                      ? "highlight-wallet"
                                      : ""
                                  }`}
                                  tabIndex={0}
                                  role="row"
                                >
                                  <td data-label="Rank">{wallet.rank}</td>
                                  <td data-label="Amount">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>{`${wallet.amount.toFixed(8)} VE`}</Tooltip>}
                                    >
                                      <span>{formatAmount(wallet.amount)} VE</span>
                                    </OverlayTrigger>
                                  </td>
                                  <td data-label="Supply">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>{`${wallet.percent.toFixed(8)}% of Total Supply`}</Tooltip>}
                                    >
                                      <span>{wallet.percent.toFixed(2)}%</span>
                                    </OverlayTrigger>
                                  </td>
                                  <td data-label="Address">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>{wallet.address}</Tooltip>}
                                    >
                                      <span
                                        className="truncated-address"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyToClipboard(wallet.address, setToastMessage, setToastVariant, setShowToast);
                                        }}
                                        onKeyDown={(e) => e.key === "Enter" && copyToClipboard(wallet.address, setToastMessage, setToastVariant, setShowToast)}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Copy address ${wallet.address}`}
                                      >
                                        {truncateAddress(wallet.address)} <FaCopy size="0.8rem" />
                                      </span>
                                    </OverlayTrigger>
                                  </td>
                                  <td data-label="Tags">
                                    {addressTags[wallet.address] ? (
                                      <Badge bg="none" className="badge-tag">
                                        {addressTags[wallet.address]}
                                      </Badge>
                                    ) : ""}
                                  </td>
                                  <td data-label="Trend" className="hide-on-mobile">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip className="trend-tooltip">
                                          {wallet.history.map((h) => `${h.date}: ${formatAmount(h.amount)} VE`).join("\n")}
                                        </Tooltip>
                                      }
                                    >
                                      <span className="d-flex align-items-center">
                                        <Sparklines
                                          data={Array(CONFIG.MAX_HISTORY_DAYS)
                                            .fill(0)
                                            .map((_, i) => wallet.history[i]?.amount || 0)}
                                          width={50}
                                          height={20}
                                          margin={2}
                                        >
                                          <SparklinesLine color="#14F1D9" style={{ fill: "none", strokeWidth: 2 }} />
                                        </Sparklines>
                                        <span
                                          className={
                                            wallet.trend === "up"
                                              ? "text-success ms-1"
                                              : wallet.trend === "down"
                                              ? "text-danger ms-1"
                                              : "text-muted ms-1"
                                          }
                                        >
                                          {wallet.trend === "up" && "↑"}
                                          {wallet.trend === "down" && "↓"}
                                          {wallet.changePercent ? wallet.changePercent.toFixed(1) : 0}%
                                        </span>
                                      </span>
                                    </OverlayTrigger>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="text-center">
                                  No wallets available for this tag or balance range.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <Row className="mt-4">
                        <Col xs={12} sm={6} className="d-flex flex-row justify-content-center mb-3 mb-sm-0">
                          <div className="me-auto">
                            <Form onSubmit={handleGoToPage} className="d-flex">
                              <Form.Control
                                type="text"
                                name="pageNo"
                                placeholder="Page"
                                className={`me-2 ${pageError ? "is-invalid" : ""}`}
                                style={{ width: "100px" }}
                                aria-label="Go to page number"
                              />
                              <Button type="submit" variant="primary" className="me-2" aria-label="Submit page number">
                                Go
                              </Button>
                              <Button
                                variant="success"
                                onClick={handleExportToCSV}
                                aria-label="Export to CSV"
                                disabled={isExporting}
                              >
                                {isExporting ? (
                                  <>
                                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                                    Exporting...
                                  </>
                                ) : (
                                  <>
                                    <FaFileCsv className="me-1" /> Export to CSV
                                  </>
                                )}
                              </Button>
                            </Form>
                          </div>
                        </Col>
                        <Col xs={12} sm={6} className="d-flex flex-row justify-content-end">
                          <UtxoPagination active={currentPage} total={totalFilteredPages} setActive={handlePageChange} />
                        </Col>
                      </Row>
                      <Row className="mt-4">
                        <Col xs={12}>
                          <h5 className="section-header">Search for a Wallet</h5>
                          <Form onSubmit={handleSearch} className="d-flex align-items-center mb-3">
                            <Form.Group controlId="searchWallet" className="me-2 flex-grow-1">
                              <Form.Control
                                type="text"
                                placeholder="Enter wallet address (e.g., vecno:qqtsqwxa...)"
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                className={!isSearchValid ? "is-invalid" : ""}
                                aria-label="Search for a wallet address"
                                aria-invalid={!isSearchValid}
                              />
                              {!isSearchValid && (
                                <Form.Text className="text-danger">
                                  Please enter a valid wallet address starting with "vecno:" and 61-63 characters long.
                                </Form.Text>
                              )}
                            </Form.Group>
                            <Button
                              variant="primary"
                              type="submit"
                              style={{ height: "38px", padding: "0 12px", display: "flex", alignItems: "center" }}
                              aria-label="Search wallet"
                            >
                              <FaSearch size="0.9rem" style={{ marginRight: "4px" }} />
                              Search
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={handleClearSearch}
                              style={{ height: "38px", padding: "0 12px", display: "flex", alignItems: "center" }}
                              className="ms-2"
                              aria-label="Clear search"
                              disabled={!searchQuery}
                            >
                              <FaTimes size="0.9rem" style={{ marginRight: "4px" }} />
                              Clear
                            </Button>
                          </Form>
                        </Col>
                      </Row>
                      {searchResults.length > 0 && (
                        <Row className="mt-2">
                          <Col xs={12}>
                            <h5 className="section-header">Search Results</h5>
                            <Button
                              variant="info"
                              size="sm"
                              onClick={() => {
                                if (searchResults[0]?.address) {
                                  const url = `${window.location.origin}/top?address=${encodeURIComponent(
                                    searchResults[0].address
                                  )}`;
                                  copyToClipboard(url, setToastMessage, setToastVariant, setShowToast);
                                } else {
                                  setToastMessage("No address available to copy.");
                                  setToastVariant("danger");
                                  setShowToast(true);
                                }
                              }}
                              className="mb-2"
                              aria-label="Copy shareable link"
                              disabled={!searchResults[0]?.address}
                            >
                              <FaLink className="me-1" /> Copy Shareable Link
                            </Button>
                            <div className="table-responsive">
                              <table className="table-common styled-table w-100 mt-2 table-sticky">
                                <thead>
                                  <tr>
                                    <th scope="col"><FaListOl className="me-1" /> Rank</th>
                                    <th scope="col"><FaWallet className="me-1" /> Amount</th>
                                    <th scope="col"><FaPercentage className="me-1" /> Supply</th>
                                    <th scope="col">Address</th>
                                    <th scope="col"><FaTag className="me-1" /> Tags</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {searchResults.map((wallet) => (
                                    <tr
                                      key={wallet.address}
                                      onClick={() => navigate(`/addresses/${wallet.address}`)}
                                      onKeyDown={(e) => e.key === "Enter" && navigate(`/addresses/${wallet.address}`)}
                                      style={{ cursor: "pointer" }}
                                      className="fade-in-row"
                                      tabIndex={0}
                                      role="row"
                                    >
                                      <td data-label="Rank">{wallet.rank}</td>
                                      <td data-label="Amount">
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={<Tooltip>{`${wallet.amount.toFixed(8)} VE`}</Tooltip>}
                                        >
                                          <span>{formatAmount(wallet.amount)} VE</span>
                                        </OverlayTrigger>
                                      </td>
                                      <td data-label="Supply">
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={<Tooltip>{`${wallet.percent.toFixed(8)}% of Total Supply`}</Tooltip>}
                                        >
                                          <span>{wallet.percent.toFixed(2)}%</span>
                                        </OverlayTrigger>
                                      </td>
                                      <td data-label="Address">
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={<Tooltip>{wallet.address}</Tooltip>}
                                        >
                                          <span
                                            className="truncated-address"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              copyToClipboard(wallet.address, setToastMessage, setToastVariant, setShowToast);
                                            }}
                                            onKeyDown={(e) => e.key === "Enter" && copyToClipboard(wallet.address, setToastMessage, setToastVariant, setShowToast)}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Copy address ${wallet.address}`}
                                          >
                                            {truncateAddress(wallet.address)} <FaCopy size="0.8rem" />
                                          </span>
                                        </OverlayTrigger>
                                      </td>
                                      <td data-label="Tags">
                                        {addressTags[wallet.address] ? (
                                          <Badge bg="none" className="badge-tag">
                                            {addressTags[wallet.address]}
                                          </Badge>
                                        ) : ""}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>
                        </Row>
                      )}
                      {searchQuery && searchResults.length === 0 && (
                        <Row className="mt-2">
                          <Col xs={12}>
                            <Alert variant="info">
                              Wallet not found in the top {CONFIG.ITEMS_PER_PAGE} addresses.
                            </Alert>
                          </Col>
                        </Row>
                      )}
                      <ToastContainer position="top-end" className="p-3">
                        <Toast
                          onClose={() => setShowToast(false)}
                          show={showToast}
                          delay={3000}
                          autohide
                          bg={toastVariant}
                          role="alert"
                          aria-live="assertive"
                        >
                          <Toast.Header>
                            <strong className="me-auto">Notification</strong>
                          </Toast.Header>
                          <Toast.Body>{toastMessage}</Toast.Body>
                        </Toast>
                      </ToastContainer>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ErrorBoundary>
  );
};

export default TopHolders;